export const REORDER_ITEM_ID_PARAM = "reorderItemId";
export const REORDER_COUPON_PARAM = "reorderCoupon";
/** Marks a link that has already been through the Auth0 bounce once. Set by the flow, never by an email. */
export const REORDER_BOUNCED_PARAM = "reorderBounced";

/** What `toSafeStrippedPath` removes from the URL once a reorder link has been read. */
export const REORDER_STRIP_PARAMS = [REORDER_ITEM_ID_PARAM, REORDER_COUPON_PARAM, REORDER_BOUNCED_PARAM];

export interface ReorderIntent {
  originalItemId: string;
  couponCode: string | null;
}

/**
 * Three outcomes, not two. "absent" (no reorder key in the URL at all) and "invalid" (a key is
 * there but malformed) mean opposite things to the flow: the first resumes whatever is still
 * in storage, the second must abandon WITHOUT reading storage. Collapsing them into one null
 * lets a malformed link fall through and redeem the item an interrupted earlier login left
 * behind — a different item and a different coupon than the one that was clicked.
 */
export type ReorderParamsResult =
  | { status: "absent" }
  | { status: "invalid" }
  | { status: "valid"; intent: ReorderIntent };

// sg-frontend-common's UuidFormatter keeps its isUuid check private, so validate here.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

//verify payload fits comma-separated UUID format
export function isReorderItemId(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() === "") return false;

  return value.split(",").every(item => UUID_PATTERN.test(item));
}

/**
 * Reads the one-click reorder params off a query string. The item id is the only thing the
 * reorder endpoint trusts, so anything malformed fails closed — including a coupon that
 * arrives with no item id, which is a reorder key the flow can never redeem.
 */
export function parseReorderParams(searchParams: URLSearchParams): ReorderParamsResult {
  const originalItemId = searchParams.get(REORDER_ITEM_ID_PARAM);
  const couponCode = searchParams.get(REORDER_COUPON_PARAM);
  if (originalItemId === null && couponCode === null) return { status: "absent" };
  if (!isReorderItemId(originalItemId)) return { status: "invalid" };
  if (!couponCode) return { status: "valid", intent: { originalItemId, couponCode: null } };

  return { status: "valid", intent: { originalItemId, couponCode } };
}

/**
 * Puts the reorder params back onto a query string for the Auth0 returnTo. Exactly one
 * caller: the login bounce, and only when sessionStorage refused the intent (see
 * useOneClickReorderFlow) — otherwise the item id stays out of the returnTo entirely.
 *
 * The bounce marker rides along because the bounce-once guard normally lives on the stored
 * intent, and this path exists precisely because nothing could be stored. Without it, a
 * browser that blocks site data (so: cookies too, so login never sticks) would bounce to
 * Auth0, come back signed out with the params intact, and bounce again forever.
 */
export function withReorderBounceParams(searchParams: URLSearchParams, intent: ReorderIntent): URLSearchParams {
  const next = new URLSearchParams(searchParams.toString());
  next.set(REORDER_ITEM_ID_PARAM, intent.originalItemId);
  next.set(REORDER_BOUNCED_PARAM, "1");
  next.delete(REORDER_COUPON_PARAM);
  if (intent.couponCode) next.set(REORDER_COUPON_PARAM, intent.couponCode);

  return next;
}

/** Whether this URL is a visitor coming back from the bounce above, still without a session. */
export function hasReorderBounceParam(searchParams: URLSearchParams): boolean {
  return searchParams.get(REORDER_BOUNCED_PARAM) === "1";
}
